import { FiberManualRecord } from '@mui/icons-material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { green, orange, red } from '@mui/material/colors';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';
import type { FC, ReactElement } from 'react';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { uploadUserColumnPreferences } from 'api/user';
import DotsLoader from 'components/DotsLoader/DotsLoader';
import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketAssigneeE } from 'declarations/enums/tickets';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { LastMessageI } from 'declarations/interfaces/tickets';
import type { Column, NoopT } from 'declarations/types/common';
import { formatAmount } from 'helpers/locale';
import { getInitials } from 'helpers/string';
import { useFetchUserColumnPreferences } from 'hooks/api/useUser';
import ApproveIcon from 'icons/ApproveIcon';
import { MessageWrapStyled } from 'pages/TicketsPage/TicketsPage.styled';

import { mapAgeToColor } from '../../helpers';
import { getLastMessageDisplayText } from '../../helpers/lastMessageDisplay';
import {
  SCHEDULE_EXCLUDED_COLUMN_IDS,
  TICKET_EXCLUDED_COLUMN_IDS,
  getColumnsForSection,
  getStorageKeyForSection,
} from '../ColumnPickerSettingsPopup/components/ColumnPickerSettings/mockedColumns';
import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';
import TicketsSorting from '../TicketsSorting/TicketsSorting';
import {
  CHECKBOX_COLUMN_WIDTH_PX,
  VIEWED_INDICATOR_COLUMN_WIDTH_PX,
  getDataColumnWidth,
  getTotalWeight,
} from '../TicketsSorting/TicketsTableConfiguration';

import TicketsListViewLoader from './components/TicketsListViewLoader';
import {
  ApprovedStyled,
  ContentStyled,
  HasAttachmentStyled,
  ListViewStyled,
} from './TicketsListView.styled';

export type TicketsListViewPropsT = {
  ticketsCount: number;
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string, payload: { roNumber: string; lineNumber: string | null }) => NoopT;
  isViewed?: boolean;
  columns: Column[];
};

type ColumnVisibilityMap = { [key: string]: boolean };

const COLUMN_LABEL_MIGRATION: Record<string, string> = {
  'RO number': 'RO',
  'Line number': 'Line',
  Priority: 'P',
  'Age (days)': 'Age',
  'Claim Age (days)': 'Claim Age',
  'Assigned to:': 'Assignee',
  'Advisor number': 'Advisor',
  'Tech number': 'Tech',
  'Account Number': 'Account',
};

const priorityIconMap: Record<string, { icon: React.ReactNode; color: string }> = {
  Critical: { icon: <KeyboardDoubleArrowUp />, color: red[900] },
  High: { icon: <KeyboardArrowUp />, color: red[500] },
  Medium: { icon: <KeyboardArrowDown />, color: orange[500] },
  Low: { icon: <KeyboardDoubleArrowDown />, color: green[500] },
};

function TruncatedWithTooltip({
  value,
  color = 'text.primary',
}: {
  value: string | null | undefined;
  color?: string;
}): ReactElement {
  const display = value != null && value !== '' ? value : '-';
  return (
    <Tooltip title={display} enterDelay={400} arrow placement="top-start">
      <Typography
        variant="body2"
        color={color}
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {display}
      </Typography>
    </Tooltip>
  );
}

const TicketsListView: FC<TicketsListViewPropsT> = ({
  ticketsPages,
  ticketsCount,
  isLoadingTickets,
  onSelectTicket,
  columns,
}) => {
  const { isMobile } = useResponsive();
  const {
    isSuccessfullyFetchedTickets,
    isFetchingNextTicketsPage,
    isFetchingTickets,
    hasNextTicketsPage,
    fetchNextTicketsPage,
    selectedRoofTop,
    selectedTicketsIds,
    setSelectedTicketsIds,
    activeTab,
    ticketListScrollPosition,
    setTicketListScrollPosition,
  } = useTickets();

  const isScheduleSection = activeTab === ActiveTabTypeE.Schedule;
  const sectionStorageKey = getStorageKeyForSection(activeTab);
  const sectionColumns = getColumnsForSection(activeTab);
  const {
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
  } = useTicketsRefinement();
  /** IntersectionObserver root must be the scrollable list pane (not the viewport), or load-more never fires reliably. */
  const [listScrollRoot, setListScrollRoot] = useState<Element | null>(null);
  const listViewScrollRestoreRef = useRef<HTMLDivElement | null>(null);
  const setListViewScrollRootRef = useCallback((node: HTMLDivElement | null): void => {
    listViewScrollRestoreRef.current = node;
    setListScrollRoot(node);
  }, []);

  const { ref: loadMoreSentinelRef, inView } = useInView({
    root: listScrollRoot ?? undefined,
    rootMargin: '160px',
    threshold: 0,
  });

  const userColumnPreferences = useFetchUserColumnPreferences({
    onSuccess: (data: string): void => {
      localStorage.setItem('visibleColumns', data);
    },
    onError: (err: ApiErrorI): void => {
      if (err?.data?.detail?.startsWith('The specified key does not exist.')) {
        const defaultColumnVisibility: ColumnVisibilityMap = {};
        columns.forEach(column => {
          defaultColumnVisibility[column.id] = true;
        });

        uploadUserColumnPreferences({ RawData: JSON.stringify(columns) });
      }
    },
  });

  const storedVisibleColumnsJSON =
    userColumnPreferences.data ??
    localStorage.getItem(sectionStorageKey) ??
    (isScheduleSection ? null : localStorage.getItem('visibleColumns'));
  const storedVisibleColumns: Column[] = useMemo(
    (): Column[] => (storedVisibleColumnsJSON ? JSON.parse(storedVisibleColumnsJSON) : []),
    [storedVisibleColumnsJSON]
  );

  const orderedColumns = useMemo((): Column[] => {
    const defaultCols = sectionColumns;
    if (
      !Array.isArray(storedVisibleColumns) ||
      storedVisibleColumns.length === 0 ||
      typeof storedVisibleColumns[0] !== 'object' ||
      storedVisibleColumns[0] === null ||
      !('label' in (storedVisibleColumns[0] as Column))
    ) {
      return defaultCols;
    }
    const migrated = (storedVisibleColumns as Column[]).map(col => ({
      ...col,
      label: COLUMN_LABEL_MIGRATION[col.label] ?? col.label,
    }));
    const storedIds = new Set(migrated.map((c): string => c.id));
    const missing = defaultCols.filter(col => !storedIds.has(col.id));
    let result = missing.length > 0 ? [...migrated, ...missing] : migrated;
    if (isScheduleSection) {
      result = result.filter(col => !SCHEDULE_EXCLUDED_COLUMN_IDS.includes(col.id));
    } else {
      result = result.filter(col => !TICKET_EXCLUDED_COLUMN_IDS.includes(col.id));
    }
    return result;
  }, [storedVisibleColumns, sectionColumns, isScheduleSection]);

  const visibleColumns = useMemo(
    (): Column[] => orderedColumns.filter((col: Column): boolean => col.visible),
    [orderedColumns]
  );

  const visibleColumnCount = visibleColumns.length + 2;

  const totalWeight = useMemo((): number => getTotalWeight(visibleColumns), [visibleColumns]);

  const handleTicketSelection = (ticketId: string): void => {
    if (selectedTicketsIds.includes(ticketId)) {
      setSelectedTicketsIds(selectedTicketsIds.filter(id => id !== ticketId));
    } else {
      setSelectedTicketsIds([...selectedTicketsIds, ticketId]);
    }
  };

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    id: string,
    roNumber: string,
    lineNumber: string | null
  ): void => {
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();
    const inputType = (target as HTMLInputElement).type;
    const isCheckboxClicked = tagName === 'input' && inputType === 'checkbox';

    if (!isCheckboxClicked) {
      onSelectTicket(id, { roNumber, lineNumber })();
    }
  };

  const handleSelectAllClick = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        const allIds = ticketsPages?.flatMap(p => p.data?.map(t => t.id) ?? []) ?? [];
        setSelectedTicketsIds(allIds);
      } else {
        setSelectedTicketsIds([]);
      }
    },
    [ticketsPages, setSelectedTicketsIds]
  );

  const totalRowCount = useMemo(
    (): number => ticketsPages?.reduce((sum, page) => sum + (page.data?.length ?? 0), 0) ?? 0,
    [ticketsPages]
  );

  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage && hasNextTicketsPage;

  useEffect((): void => {
    if (inView && hasNextTicketsPage && !isFetchingNextTicketsPage) {
      fetchNextTicketsPage();
    }
  }, [fetchNextTicketsPage, hasNextTicketsPage, inView, isFetchingNextTicketsPage]);

  useEffect((): void => {
    const el = listViewScrollRestoreRef.current;
    if (!isMobile && el && ticketListScrollPosition > 0 && !isLoadingTickets) {
      requestAnimationFrame((): void => {
        el.scrollTop = ticketListScrollPosition;
        setTicketListScrollPosition(0);
      });
    }
  }, [isMobile, ticketListScrollPosition, setTicketListScrollPosition, isLoadingTickets]);

  useEffect((): void => {
    if (!localStorage.getItem('visibleColumns')) {
      localStorage.setItem('visibleColumns', JSON.stringify(columns));
    }
  }, [columns]);

  useEffect((): void => {
    setSelectedTicketsIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRoofTop,
    isOpenStatusFilterActive,
    isCloseStatusFilterActive,
    isUnder24HoursFilterActive,
    isOver24HoursFilterActive,
    isAssignedToMeFilterActive,
    isPastMonthFilterActive,
    isPast30DaysFilterActive,
    isArchivedFilterActive,
  ]);

  const getLastMessageText = (lastMessage: LastMessageI): string =>
    getLastMessageDisplayText(lastMessage);

  if (isSuccessfullyFetchedTickets && ticketsCount === 0) {
    return (
      <ListViewStyled>
        <ContentStyled data-testid="tickets-list">
          <Table
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': { px: 1, overflow: 'hidden', whiteSpace: 'nowrap' },
            }}
          >
            <colgroup>
              <col style={{ width: CHECKBOX_COLUMN_WIDTH_PX }} />
              <col style={{ width: VIEWED_INDICATOR_COLUMN_WIDTH_PX }} />
              {visibleColumns.map(
                (col: Column): JSX.Element => (
                  <col key={col.id} style={{ width: getDataColumnWidth(col.label, totalWeight) }} />
                )
              )}
            </colgroup>
            <TicketsSorting
              selectedTicketsIds={selectedTicketsIds}
              columns={orderedColumns}
              dataColumnWidthsPx={null}
              totalWeight={totalWeight}
              numSelected={selectedTicketsIds.length}
              rowCount={0}
              onSelectAllClick={handleSelectAllClick}
            />
          </Table>
          <MessageWrapStyled>
            <Typography variant="h6" color="text.primary" align="center">
              Tickets not found
            </Typography>
          </MessageWrapStyled>
        </ContentStyled>
      </ListViewStyled>
    );
  }

  return (
    <ListViewStyled ref={setListViewScrollRootRef}>
      <Table
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          '& .MuiTableCell-root': { px: 1, overflow: 'hidden', whiteSpace: 'nowrap' },
        }}
      >
        <colgroup>
          <col style={{ width: CHECKBOX_COLUMN_WIDTH_PX }} />
          <col style={{ width: VIEWED_INDICATOR_COLUMN_WIDTH_PX }} />
          {visibleColumns.map((col: Column) => (
            <col key={col.id} style={{ width: getDataColumnWidth(col.label, totalWeight) }} />
          ))}
        </colgroup>
        <TicketsSorting
          selectedTicketsIds={selectedTicketsIds}
          columns={orderedColumns}
          dataColumnWidthsPx={null}
          totalWeight={totalWeight}
          numSelected={selectedTicketsIds.length}
          rowCount={totalRowCount}
          onSelectAllClick={handleSelectAllClick}
        />
        {isLoadingTickets && !isFetchingNextTicketsPage ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={visibleColumnCount} sx={{ border: 'none', p: 0 }}>
                <TicketsListViewLoader />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody data-testid="tickets-list">
            {ticketsPages?.map((page, pageIndex) => (
              <Fragment key={`page-${pageIndex}`}>
                {page.data?.map(
                  (
                    {
                      id,
                      roNumber,
                      lineNumber,
                      vin,
                      age,
                      statusId,
                      assigneeName,
                      categoryName,
                      subCategory,
                      requestSubCategoryName,
                      isApprovedByDealer,
                      isViewed,
                      lastMessage,
                      hasAttachment,
                      amount,
                      claimStatus,
                      ticketPriority,
                      techNumber,
                      advisorNumber,
                      claimAge,
                      closedROAge,
                      updatedByUser,
                      updatedDateTime,
                      dmsScheduleAccountNumber,
                    },
                    rowIndex
                  ) => (
                    <TableRow
                      key={id}
                      hover
                      className={`ticket-row-${rowIndex}`}
                      onDoubleClick={(event: React.MouseEvent<HTMLTableRowElement>): void =>
                        handleRowClick(event, id, roNumber, lineNumber)
                      }
                      sx={{
                        cursor: 'pointer',
                        ...(!isViewed && {
                          backgroundColor: theme => theme.palette.action.hover,
                          fontWeight: 600,
                        }),
                        ...(ticketPriority === 'Critical' && {
                          borderLeft: theme => `3px solid ${theme.palette.error.main}`,
                        }),
                      }}
                    >
                      <TableCell padding="checkbox" sx={{ width: CHECKBOX_COLUMN_WIDTH_PX }}>
                        <Checkbox
                          size="medium"
                          color="primary"
                          checked={selectedTicketsIds.includes(id)}
                          onChange={(): void => {
                            handleTicketSelection(id);
                          }}
                          sx={{ width: 40, height: 40 }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          boxShadow: theme => `0 1px 0 0 ${theme.palette.divider}`,
                          width: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
                          minWidth: VIEWED_INDICATOR_COLUMN_WIDTH_PX,
                          padding: '0 4px',
                          textAlign: 'center',
                        }}
                      >
                        {!isViewed && (
                          <FiberManualRecord
                            sx={{
                              color: 'primary.main',
                              fontSize: 12,
                              display: 'block',
                            }}
                          />
                        )}
                      </TableCell>
                      {orderedColumns
                        .filter((c): boolean => c.visible)
                        .map((col: Column) => {
                          if (col.label === 'P') {
                            return (
                              <TableCell key={col.id} align="center">
                                <Box
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  title={ticketPriority ?? ''}
                                >
                                  {((): React.ReactNode => {
                                    const mapped = priorityIconMap[ticketPriority];
                                    if (!mapped) return <Typography variant="body2">-</Typography>;
                                    return (
                                      <Box
                                        display="flex"
                                        alignItems="center"
                                        sx={{ color: mapped.color }}
                                      >
                                        {mapped.icon}
                                      </Box>
                                    );
                                  })()}
                                </Box>
                              </TableCell>
                            );
                          }
                          if (col.label === 'RO') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={roNumber} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'VIN') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={vin} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Line') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={lineNumber || '-'} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Age') {
                            return (
                              <TableCell key={col.id} sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  color={mapAgeToColor(age) as 'text.primary'}
                                >
                                  {age}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (col.label === 'Claim Age') {
                            return (
                              <TableCell key={col.id} sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  color={
                                    claimAge
                                      ? (mapAgeToColor(claimAge) as 'text.primary')
                                      : 'text.primary'
                                  }
                                >
                                  {claimAge || '-'}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (col.label === 'Closed RO Age') {
                            return (
                              <TableCell key={col.id} sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  color={
                                    closedROAge
                                      ? (mapAgeToColor(closedROAge) as 'text.primary')
                                      : 'text.primary'
                                  }
                                >
                                  {closedROAge || '-'}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (col.label === 'Status') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={statusId} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Assignee') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip
                                  value={assigneeName}
                                  color={
                                    assigneeName === TicketAssigneeE.WarrCloud
                                      ? 'secondary'
                                      : 'text.primary'
                                  }
                                />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Category') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={categoryName} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Last Comment') {
                            return (
                              <TableCell key={col.id}>
                                {lastMessage !== undefined && (
                                  <TruncatedWithTooltip value={getLastMessageText(lastMessage)} />
                                )}
                              </TableCell>
                            );
                          }
                          if (col.label === 'Claim Status') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={claimStatus} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Amount') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={formatAmount(amount)} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Advisor') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={advisorNumber || '-'} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Tech') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip value={techNumber || '-'} />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Attachment') {
                            return (
                              <TableCell key={col.id}>
                                {hasAttachment && (
                                  <HasAttachmentStyled>
                                    <ApproveIcon size={isMobile ? 'xl' : '3xl'} />
                                  </HasAttachmentStyled>
                                )}
                              </TableCell>
                            );
                          }
                          if (col.label === 'Approved') {
                            return (
                              <TableCell key={col.id}>
                                {isApprovedByDealer && (
                                  <ApprovedStyled>
                                    <ApproveIcon size={isMobile ? 'xl' : '3xl'} />
                                  </ApprovedStyled>
                                )}
                              </TableCell>
                            );
                          }
                          if (col.label === 'Subcategory') {
                            return (
                              <TableCell key={col.id}>
                                <TruncatedWithTooltip
                                  value={subCategory ?? requestSubCategoryName ?? '-'}
                                />
                              </TableCell>
                            );
                          }
                          if (col.label === 'Account') {
                            return (
                              <TableCell key={col.id}>
                                <Typography variant="body2" color="text.primary">
                                  {dmsScheduleAccountNumber || '-'}
                                </Typography>
                              </TableCell>
                            );
                          }
                          if (col.label === 'Audit Trail') {
                            return (
                              <TableCell key={col.id}>
                                <Tooltip title={updatedByUser?.displayName} placement="right" arrow>
                                  <Box display="inline-block">
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {updatedByUser?.displayName
                                        ? getInitials(updatedByUser?.displayName)
                                        : '-'}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                                <Typography variant="body2" color="text.primary">
                                  {updatedDateTime
                                    ? format(new Date(updatedDateTime), 'dd MMM yyyy h:mm a')
                                    : null}
                                </Typography>
                              </TableCell>
                            );
                          }
                          return <TableCell key={col.id} />;
                        })}
                    </TableRow>
                  )
                )}
              </Fragment>
            ))}
          </TableBody>
        )}
      </Table>
      <span
        style={{
          display: 'block',
          width: '100%',
          height: '20px',
        }}
        ref={loadMoreSentinelRef}
      />
      {shouldLoadMore && <DotsLoader />}
    </ListViewStyled>
  );
};

export default TicketsListView;
